import React, { useState, useMemo } from "react";
import { LogIn, LayoutDashboard, Users, FlaskConical, Pill, Stethoscope, Search, BookOpen, Smartphone, KeyRound, ChevronRight } from "lucide-react";

/**
 * In-app Help Center — clean sidebar nav + styled content cards.
 * Source of truth mirrors /app/FEATURES.md (May 30 2026).
 */
const SECTIONS = [
  { id: "overview",  label: "Overview",          icon: BookOpen,        kind: "header" },
  { id: "login",     label: "Login & Accounts",  icon: LogIn,           kind: "page" },
  { id: "dashboard", label: "Dashboard",         icon: LayoutDashboard, kind: "page" },
  { id: "patient",   label: "Patients",          icon: Users,           kind: "page" },
  { id: "regimens",  label: "Regimens",          icon: FlaskConical,    kind: "page" },
  { id: "drugs",     label: "Drugs Library",     icon: Pill,            kind: "page" },
  { id: "provider",  label: "Providers",         icon: Stethoscope,     kind: "page" },
  { id: "pwa",       label: "Patient mobile app", icon: Smartphone,     kind: "page" },
  { id: "accounts",  label: "Test accounts",     icon: KeyRound,        kind: "page" },
];

const Bullet = ({ children }) => (
  <li className="flex items-start gap-2 text-stone-700">
    <ChevronRight className="w-4 h-4 mt-1 text-brand-500 shrink-0" />
    <span>{children}</span>
  </li>
);

const Card = ({ title, eyebrow, children, testid }) => (
  <section className="bg-white border border-stone-200 rounded-xl shadow-soft overflow-hidden" data-testid={testid}>
    {eyebrow && <div className="px-5 pt-4 text-[11px] uppercase tracking-wider text-stone-500 font-semibold">{eyebrow}</div>}
    {title && <h3 className="px-5 pt-2 pb-3 font-display font-semibold text-stone-900 text-lg">{title}</h3>}
    <div className="px-5 pb-5 text-sm leading-relaxed">{children}</div>
  </section>
);

export default function HelpPage() {
  const [active, setActive] = useState("overview");
  const [query, setQuery] = useState("");

  const filteredSections = useMemo(() => {
    if (!query.trim()) return SECTIONS;
    const q = query.toLowerCase();
    return SECTIONS.filter((s) => s.label.toLowerCase().includes(q));
  }, [query]);

  return (
    <div className="p-8 max-w-[1600px]" data-testid="help-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-stone-900">Help Center</h1>
        <p className="text-stone-500 mt-1 text-sm">A plain-English reference for every screen and capability in OncoCadence.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Sidebar */}
        <aside className="bg-white border border-stone-200 rounded-xl shadow-soft h-fit p-3" data-testid="help-sidebar">
          <div className="px-2 py-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search help…"
                className="w-full pl-8 pr-3 py-1.5 rounded-md border border-stone-300 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                data-testid="help-search-input"
              />
            </div>
          </div>
          <nav className="mt-2 space-y-0.5" data-testid="help-nav">
            {filteredSections.map((s) => {
              const I = s.icon;
              const activeCls = active === s.id ? "bg-brand-100 text-brand-700" : "text-stone-700 hover:bg-stone-50";
              return (
                <button
                  key={s.id}
                  onClick={() => setActive(s.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${activeCls}`}
                  data-testid={`help-nav-${s.id}`}
                >
                  <I className="w-4 h-4 shrink-0" />
                  <span className="truncate">{s.label}</span>
                </button>
              );
            })}
            {filteredSections.length === 0 && (
              <div className="px-3 py-2 text-xs text-stone-500 italic">No sections match "{query}".</div>
            )}
          </nav>
        </aside>

        {/* Content */}
        <main className="space-y-5" data-testid={`help-content-${active}`}>
          {active === "overview" && <OverviewSection />}
          {active === "login"     && <LoginSection />}
          {active === "dashboard" && <DashboardSection />}
          {active === "patient"   && <PatientSection />}
          {active === "regimens"  && <RegimensSection />}
          {active === "drugs"     && <DrugsSection />}
          {active === "provider"  && <ProviderSection />}
          {active === "pwa"       && <PwaSection />}
          {active === "accounts"  && <AccountsSection />}
        </main>
      </div>
    </div>
  );
}


/* ------------------------- Sections ------------------------- */
function OverviewSection() {
  return (
    <>
      <Card eyebrow="Welcome" title="What is OncoCadence?" testid="help-card-welcome">
        <p>OncoCadence is a chemotherapy-calendar automation platform for oncology teams. Providers build regimens, generate patient calendars, log side-effects, run analytics, and collaborate across an approval workflow. Patients use a mobile-responsive companion app to see their schedule, log how they're feeling, and message their care team.</p>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Card eyebrow="At a glance" title="What's in this app">
          <ul className="space-y-1.5">
            <Bullet>5 user roles — Admin, Organizer, Doctor, Nurse, Patient</Bullet>
            <Bullet>189-drug library + 55 regimens (4 standard + 50 sample protocols)</Bullet>
            <Bullet>Live patient analytics + AI Insights (Claude Sonnet 4.5)</Bullet>
            <Bullet>25 palette themes (light & dark) with per-user persistence</Bullet>
            <Bullet>SMTP welcome / reset / decision emails</Bullet>
            <Bullet>ICS calendar export per patient</Bullet>
          </ul>
        </Card>
        <Card eyebrow="Use this Help Center" title="How to navigate">
          <ul className="space-y-1.5">
            <Bullet>Use the left sidebar to jump between screens.</Bullet>
            <Bullet>The search box filters sections by title.</Bullet>
            <Bullet>Each section is a plain-English walkthrough — no code, no jargon.</Bullet>
            <Bullet>For the full Markdown reference, see <code className="px-1 py-0.5 rounded bg-stone-100">/app/FEATURES.md</code>.</Bullet>
          </ul>
        </Card>
      </div>
    </>
  );
}

function LoginSection() {
  return (
    <>
      <Card eyebrow="Section 1" title="Login, Registration & Password Recovery">
        <p>The first screen anyone sees. It controls who can get into OncoCadence and what they're allowed to do once inside.</p>
      </Card>
      <Card title="Sign in">
        <ul className="space-y-1.5">
          <Bullet>Email + password — both required.</Bullet>
          <Bullet>Eye toggle to show/hide the password while typing.</Bullet>
          <Bullet>Bad credentials get a single neutral error ("Email or password incorrect") — never reveals which one is wrong, blocking enumeration.</Bullet>
          <Bullet>After login the user is routed automatically — providers to the portal, patients to the mobile PWA.</Bullet>
          <Bullet>JWT token is stored in browser local-storage with a 24-hour lifetime.</Bullet>
        </ul>
      </Card>
      <Card title="Forgot password">
        <ul className="space-y-1.5">
          <Bullet>One field: email.</Bullet>
          <Bullet>The response is identical whether or not the email is on file — anti-enumeration.</Bullet>
          <Bullet>The reset email contains a one-time temporary password + a link to sign in.</Bullet>
          <Bullet>On next sign-in the user is forced to the Change Password screen.</Bullet>
        </ul>
      </Card>
      <Card title="Change password (forced)">
        <ul className="space-y-1.5">
          <Bullet>Three fields: current, new, confirm — each with its own show/hide toggle.</Bullet>
          <Bullet>Strength rule: ≥ 8 characters, mix of letters + numbers.</Bullet>
          <Bullet>On success the user lands on the dashboard.</Bullet>
        </ul>
      </Card>
      <Card title="Public patient registration">
        <ul className="space-y-1.5">
          <Bullet>Only the `patient` role can self-register; providers must be created by an admin.</Bullet>
          <Bullet>Fields: name, email, password, optional phone.</Bullet>
          <Bullet>On submit the patient lands on the mobile home screen immediately.</Bullet>
        </ul>
      </Card>
      <Card title="Welcome emails">
        <ul className="space-y-1.5">
          <Bullet>Every account created from inside the portal triggers an HTML welcome email.</Bullet>
          <Bullet>Contains email, temporary password, role, and a deep link to sign in.</Bullet>
          <Bullet>The temp password is also shown once in the admin UI so they can share it through a secure channel.</Bullet>
        </ul>
      </Card>
    </>
  );
}

function DashboardSection() {
  return (
    <>
      <Card eyebrow="Section 2" title="Dashboard">
        <p>The landing screen after a provider signs in. It's the daily "what should I look at today" summary.</p>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Card title="Hero strip">
          <ul className="space-y-1.5">
            <Bullet>Personalized greeting + today's date.</Bullet>
            <Bullet>Role + specialty badge.</Bullet>
            <Bullet>Theme picker (25 palettes — light & dark).</Bullet>
          </ul>
        </Card>
        <Card title="KPI cards">
          <ul className="space-y-1.5">
            <Bullet>Active Patients</Bullet>
            <Bullet>Today's Events</Bullet>
            <Bullet>Pending Approvals</Bullet>
            <Bullet>Side-effects reported (last 7 days, with week-over-week change)</Bullet>
          </ul>
        </Card>
        <Card title="Today's events">
          <ul className="space-y-1.5">
            <Bullet>Every event scheduled for today across this provider's patients.</Bullet>
            <Bullet>Patient name, event-type icon (💉/🩺/🧪/☢️/🟢), drug print-name, route, time.</Bullet>
            <Bullet>Click any row to jump to that patient's detail page.</Bullet>
          </ul>
        </Card>
        <Card title="This week + activity feed">
          <ul className="space-y-1.5">
            <Bullet>This-week list — same structure, grouped by date.</Bullet>
            <Bullet>Recent activity — last 10 things the team did across patients / regimens / approvals.</Bullet>
          </ul>
        </Card>
      </div>
    </>
  );
}

function PatientSection() {
  return (
    <>
      <Card eyebrow="Section 3" title="Patients">
        <p>The clinical hub. Everything a provider does for a specific patient is reachable from here.</p>
      </Card>
      <Card title="Patients list page">
        <ul className="space-y-1.5">
          <Bullet>Searchable, sortable grid of every patient you're allowed to see.</Bullet>
          <Bullet>Each card shows avatar, name, status pill, MRN (Hash icon), email, phone, and an Edit pencil.</Bullet>
          <Bullet>Top toolbar — search box + "Add patient" button.</Bullet>
        </ul>
      </Card>
      <Card title="Add Patient modal — required & optional fields">
        <ul className="space-y-1.5">
          <Bullet><b>Required:</b> First name, Last name, Email (used for welcome email + patient PWA login).</Bullet>
          <Bullet><b>Optional:</b> MRN (auto-generated if blank), Phone, Gender, Date of birth, Address, <b>City</b>, <b>State</b> (USPS 2-letter dropdown of 50 states + DC + 5 territories), <b>Zip</b>.</Bullet>
          <Bullet>On submit: patient record + paired user account + welcome email with temp password.</Bullet>
        </ul>
      </Card>
      <Card title="Patient detail — two toggle tabs">
        <ul className="space-y-1.5">
          <Bullet><b>Tab 1 · Patient details</b> — profile card, calendars list, side-effect log with severity / trend / frequency chips, "View all effects" full-screen search modal.</Bullet>
          <Bullet><b>Tab 2 · ChemoAnalytics</b> — live banner showing N reported effects + compliance% + wellness score.</Bullet>
          <Bullet><b>AI Insights panel</b> at the top of the analytics tab — Claude Sonnet 4.5 summarises the patient's log into patterns, trends (↑ ↓ →), recommendations, and severity-tinted risk flags. 6-hour cache + Re-analyse button.</Bullet>
          <Bullet>6 wellness component bars (Physical / Mental / Emotional / Sleep / Nutrition / Energy).</Bullet>
          <Bullet>Weekly side-effects line chart, effects-distribution pie chart, medication adherence bars, recovery milestones timeline.</Bullet>
        </ul>
      </Card>
      <Card title="Side-effect catalog">
        <ul className="space-y-1.5">
          <Bullet>32-item dictionary across 6 categories (Digestive, Neurological, Hematological, Dermatological, Respiratory, Other).</Bullet>
          <Bullet>Severity chip: 🟢 Mild · 🟡 Moderate · 🟠 Severe · 🔴 Painful.</Bullet>
          <Bullet>Trend arrow: ↑ worsening · ↓ improving · → stable.</Bullet>
          <Bullet>Frequency: continuous / intermittent / daily / weekly.</Bullet>
        </ul>
      </Card>
    </>
  );
}

function RegimensSection() {
  return (
    <>
      <Card eyebrow="Section 4" title="Regimens">
        <p>The protocol library. Anything that gets repeated across patients — drug combos, dose schedules, cycle structure — lives here.</p>
      </Card>
      <Card title="Regimens list">
        <ul className="space-y-1.5">
          <Bullet>Card grid: name · disease type · cycle count · drug count · owner · visibility · approval-status pill.</Bullet>
          <Bullet>Search box + filters: Status (All / Approved / Pending / Draft), Owner (Mine / Standard / All).</Bullet>
        </ul>
      </Card>
      <Card title="Regimen detail + builder">
        <ul className="space-y-1.5">
          <Bullet>10-column drug table: Drug, Brand, Dose, Route (icon), Site, Days Pattern, Cycle #, Cycle Length, Print Name, Notes.</Bullet>
          <Bullet>Cycle tabs across the top let you flip between Cycle 1, 2, 3, etc.</Bullet>
          <Bullet>Auto schedule-fingerprint — if the proposed combo already exists, save returns 409 (prevents silent duplicates).</Bullet>
        </ul>
      </Card>
      <Card title="Approval workflow">
        <ul className="space-y-1.5">
          <Bullet>Draft → Submit for approval → admin/organizer reviews diff vs. last approved snapshot → Approve / Reject.</Bullet>
          <Bullet>"Restore to last approved" button reverts a working copy back to the signed snapshot.</Bullet>
          <Bullet>Standard regimens are visible org-wide; Personal regimens only to the owner.</Bullet>
        </ul>
      </Card>
    </>
  );
}

function DrugsSection() {
  return (
    <>
      <Card eyebrow="Section 5" title="Drugs Library">
        <p>The single source of truth for every drug the app knows about.</p>
      </Card>
      <Card title="Drugs list + detail">
        <ul className="space-y-1.5">
          <Bullet>Each card: name, brand names, default route, default location, system/personal badge.</Bullet>
          <Bullet>Search bar + filters: All / System / Personal / Pending Request.</Bullet>
          <Bullet>Copy-to-personal clones a system drug into your personal library so you can tweak.</Bullet>
        </ul>
      </Card>
      <Card title="Drug request flow">
        <ul className="space-y-1.5">
          <Bullet>Non-admins who can't find a drug submit a request — it becomes a row in the Approvals queue.</Bullet>
          <Bullet>On approval the drug joins the system library and the requester is notified by email.</Bullet>
          <Bullet>On rejection the requester is emailed the admin's reason.</Bullet>
        </ul>
      </Card>
      <Card title="How drugs feed the rest of the app">
        <ul className="space-y-1.5">
          <Bullet>Add-Event modal's drug dropdown loads the library once and filters client-side.</Bullet>
          <Bullet>Regimen builder's drug picker uses the same library.</Bullet>
          <Bullet>Print-name, default route, default location auto-fill when a drug is selected.</Bullet>
        </ul>
      </Card>
    </>
  );
}

function ProviderSection() {
  return (
    <>
      <Card eyebrow="Section 6" title="Provider">
        <p>The team management area. Admins and Organizers manage who has access and what they can see.</p>
      </Card>
      <Card title="Providers list">
        <ul className="space-y-1.5">
          <Bullet>Each card: avatar, name, role badge, specialty (Briefcase), department (Building), phone, office address (MapPin), inline role-change dropdown.</Bullet>
          <Bullet>Self-demotion below super-user is blocked.</Bullet>
        </ul>
      </Card>
      <Card title="Add Provider — all attributes">
        <ul className="space-y-1.5">
          <Bullet><b>Required:</b> Full Name, Email, Role (Doctor / Nurse / Admin / Organizer).</Bullet>
          <Bullet><b>Optional:</b> Phone, Specialty, <b>Department</b>, <b>Office Address</b>, <b>Office Building / Follow-up Directions</b> (multi-line), <b>City</b>, <b>State</b> (USPS dropdown), <b>Zip</b>, Password (auto-generated if blank).</Bullet>
        </ul>
      </Card>
      <Card title="Roles & permissions">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-stone-500 uppercase">
              <tr><th className="text-left pb-1.5 pr-3">Role</th><th className="text-left pb-1.5">Can do</th></tr>
            </thead>
            <tbody className="text-stone-700">
              <tr className="border-t border-stone-100"><td className="py-1.5 pr-3 font-semibold">Admin</td><td>Everything — manage providers, approve regimens, drug requests, reschedule requests, edit any patient/regimen.</td></tr>
              <tr className="border-t border-stone-100"><td className="py-1.5 pr-3 font-semibold">Organizer</td><td>Same as Admin for clinical operations; cannot change other admins' core roles.</td></tr>
              <tr className="border-t border-stone-100"><td className="py-1.5 pr-3 font-semibold">Doctor</td><td>Add patients, create/edit calendars for own patients, log side-effects, submit regimens for approval.</td></tr>
              <tr className="border-t border-stone-100"><td className="py-1.5 pr-3 font-semibold">Nurse</td><td>Same as Doctor except cannot edit MRN or change patient email.</td></tr>
              <tr className="border-t border-stone-100"><td className="py-1.5 pr-3 font-semibold">Patient</td><td>See own profile, calendar, log own side-effects, message the care team.</td></tr>
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

function PwaSection() {
  return (
    <>
      <Card eyebrow="Appendix" title="Patient mobile app (PWA)">
        <p>When a patient signs in they land on a touch-optimised mobile experience.</p>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Card title="Home + Schedule">
          <ul className="space-y-1.5">
            <Bullet>Next-event card with countdown.</Bullet>
            <Bullet>Today's regimen quick-glance.</Bullet>
            <Bullet>Schedule tab: Upcoming / Past lists.</Bullet>
          </ul>
        </Card>
        <Card title="Side-effects + Messages">
          <ul className="space-y-1.5">
            <Bullet>Chip-tag entry + free-text notes (same catalog as the provider view).</Bullet>
            <Bullet>Bidirectional chat with the care team (provider selector + 5-sec polling).</Bullet>
          </ul>
        </Card>
        <Card title="Profile">
          <ul className="space-y-1.5">
            <Bullet>Change password, regional preferences (language / country / timezone).</Bullet>
            <Bullet>Holidays the patient observes — auto-blocks scheduling when the provider opts in.</Bullet>
          </ul>
        </Card>
      </div>
    </>
  );
}

function AccountsSection() {
  return (
    <>
      <Card eyebrow="Appendix" title="Test accounts">
        <p>All accounts below are auto-seeded on backend startup. Passwords never change between deploys.</p>
      </Card>
      <Card title="Provider portal accounts">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-stone-500 uppercase">
              <tr><th className="text-left pb-1.5 pr-3">Role</th><th className="text-left pb-1.5 pr-3">Email</th><th className="text-left pb-1.5">Password</th></tr>
            </thead>
            <tbody className="text-stone-700 font-mono text-xs">
              <tr className="border-t border-stone-100"><td className="py-1.5 pr-3 font-semibold font-sans">Admin</td><td className="pr-3">admin@chemo.app</td><td>Admin@123</td></tr>
              <tr className="border-t border-stone-100"><td className="py-1.5 pr-3 font-semibold font-sans">Doctor</td><td className="pr-3">doctor@chemo.app</td><td>Doctor@123</td></tr>
              <tr className="border-t border-stone-100"><td className="py-1.5 pr-3 font-semibold font-sans">Nurse</td><td className="pr-3">nurse@chemo.app</td><td>Nurse@123</td></tr>
            </tbody>
          </table>
        </div>
      </Card>
      <Card title="Patient accounts">
        <ul className="space-y-1.5">
          <Bullet><b>Main demo:</b> <code className="font-mono">patient@chemo.app</code> / <code className="font-mono">Patient@123</code> (assigned to John Smith with an active FOLFOX calendar).</Bullet>
          <Bullet><b>25 demo patients:</b> <code className="font-mono">demo.patient01@chemo.app</code> through <code className="font-mono">demo.patient25@chemo.app</code>, all password <code className="font-mono">Patient@123</code>. MRNs <code className="font-mono">MRN-200001</code>-<code className="font-mono">MRN-200025</code>.</Bullet>
        </ul>
      </Card>
    </>
  );
}
